import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Home, User, Phone, HeartPulse, Car, Save, X, Search, AlertTriangle, Plus } from "lucide-react";
import { toast } from "sonner";

/**
 * R154 — Rich unit records: occupancy, tenant, emergency contact, medical alerts,
 * vehicles. Sidebar filter by occupancy + search + "has-alert" filter for the
 * evacuation roll.
 */
export default function UnitsManagementPage() {
  const { properties, propertyId } = useProperty();
  const [scope, setScope] = useState(propertyId || properties[0]?.id || "");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [occupancy, setOccupancy] = useState("all");
  const [hasAlert, setHasAlert] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (properties.length && !scope) setScope(properties[0].id);
  }, [properties, scope]);

  const load = useCallback(async () => {
    if (!scope) return;
    const params = { property_id: scope };
    if (occupancy !== "all") params.occupancy = occupancy;
    if (hasAlert) params.has_alert = true;
    if (search) params.search = search;
    try {
      const { data } = await api.get("/admin/units", { params });
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load units"); }
  }, [scope, occupancy, hasAlert, search]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      const payload = {
        occupancy: editing.occupancy,
        emergency_contact: editing.emergency_contact,
        tenant: editing.tenant,
        landlord_contact: editing.landlord_contact,
        medical_alerts: editing.medical_alerts,
        vehicles: editing.vehicles,
        notes: editing.notes,
      };
      await api.patch(`/admin/units/${editing.id}`, payload);
      toast.success(`Unit ${editing.unit_number} updated`);
      setEditing(null);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Save failed"); }
    finally { setBusy(false); }
  };

  const addAlert = () => {
    if (!editing) return;
    setEditing({ ...editing,
      medical_alerts: [...(editing.medical_alerts || []),
                        { kind: "mobility", note: "" }] });
  };

  const scopeName = useMemo(
    () => properties.find((p) => p.id === scope)?.name || scope,
    [properties, scope],
  );

  return (
    <div className="space-y-4" data-testid="units-page">
      <PageHeader
        eyebrow="Community"
        title="Units & Occupants"
        subtitle="Owner vs tenant, emergency contacts, medical alerts, vehicles — the roll first responders will ask for."
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ["Total", counts.total ?? "—", "text-white"],
          ["Owner-occupied", counts.owner_occupied ?? "—", "text-emerald-300"],
          ["Rented", counts.rented ?? "—", "text-orange-300"],
          ["Medical alerts", counts.medical_alerts ?? "—", "text-rose-300"],
        ].map(([k, v, cls]) => (
          <Card key={k} className="border-white/10 p-3">
            <div className="overline text-[10px]">{k}</div>
            <div className={`text-2xl font-semibold tabular-nums ${cls}`}>{v}</div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="w-56 h-9 bg-white/[0.04] border-white/10"
                          data-testid="units-scope-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
            {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={occupancy} onValueChange={setOccupancy}>
          <SelectTrigger className="w-44 h-9 bg-white/[0.04] border-white/10"
                          data-testid="units-occupancy-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
            <SelectItem value="all">All occupancy</SelectItem>
            <SelectItem value="owner_occupied">Owner-occupied</SelectItem>
            <SelectItem value="rented">Rented</SelectItem>
            <SelectItem value="vacant">Vacant</SelectItem>
          </SelectContent>
        </Select>
        <Button variant={hasAlert ? "default" : "outline"}
                onClick={() => setHasAlert(!hasAlert)}
                className={`h-9 ${hasAlert ? "bg-rose-500 hover:bg-rose-600" : "border-white/10"}`}
                data-testid="units-alerts-filter">
          <HeartPulse className="h-4 w-4 mr-1" /> Alerts only
        </Button>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search unit or resident…"
                  className="pl-9 bg-white/[0.04] border-white/10 h-9"
                  data-testid="units-search" />
        </div>
      </div>

      {/* Table */}
      <Card className="p-0 border-white/10 overflow-hidden" data-testid="units-table">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-xs">
            <tr>
              <th className="text-left px-3 py-2 text-white/60">Unit</th>
              <th className="text-left px-3 py-2 text-white/60">Type</th>
              <th className="text-left px-3 py-2 text-white/60">Occupancy</th>
              <th className="text-left px-3 py-2 text-white/60">Owner / Tenant</th>
              <th className="text-left px-3 py-2 text-white/60">Emergency</th>
              <th className="text-left px-3 py-2 text-white/60">Alerts</th>
              <th className="text-right px-3 py-2 text-white/60"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-white/40 text-sm">
                No units match. Try clearing filters or backfill units from `/admin/dues`.
              </td></tr>
            ) : items.map((u) => (
              <tr key={u.id} className="border-t border-white/5"
                  data-testid={`unit-row-${u.unit_number}`}>
                <td className="px-3 py-2 font-mono">{u.unit_number}</td>
                <td className="px-3 py-2 text-xs">{u.unit_type || "—"}</td>
                <td className="px-3 py-2">
                  <Badge variant="outline" className={`text-[10px] ${
                    u.occupancy === "owner_occupied" ? "border-emerald-500/40 text-emerald-300" :
                    u.occupancy === "rented" ? "border-orange-500/40 text-orange-300" :
                    u.occupancy === "vacant" ? "border-white/20 text-white/50" :
                    "border-white/10 text-white/40"
                  }`}>{u.occupancy || "—"}</Badge>
                </td>
                <td className="px-3 py-2 text-xs">
                  {u.owner?.name || <span className="text-white/30">—</span>}
                  {u.tenant?.name && <div className="text-orange-300/80">🗝 {u.tenant.name}</div>}
                </td>
                <td className="px-3 py-2 text-xs">
                  {u.emergency_contact?.name ? (
                    <>{u.emergency_contact.name}<br/>
                      <span className="text-white/40">{u.emergency_contact.phone || ""}</span></>
                  ) : <span className="text-white/30">—</span>}
                </td>
                <td className="px-3 py-2">
                  {(u.medical_alerts || []).length > 0 ? (
                    <div className="flex gap-1 flex-wrap">
                      {u.medical_alerts.map((a, i) => (
                        <Badge key={i} variant="outline"
                                className="border-rose-500/40 text-rose-300 bg-rose-500/10 text-[10px]">
                          <AlertTriangle className="h-2.5 w-2.5 mr-1" /> {a.kind}
                        </Badge>
                      ))}
                    </div>
                  ) : <span className="text-white/30 text-xs">—</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setEditing({ ...u })}
                          className="text-xs px-2 py-1 rounded-md border border-orange-500/40 text-orange-300 hover:bg-orange-500/10"
                          data-testid={`unit-edit-${u.unit_number}`}>
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-2xl max-h-[90vh] overflow-y-auto"
                       data-testid="unit-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Unit {editing?.unit_number}</DialogTitle>
            <DialogDescription className="text-xs text-white/60">
              Emergency contact + medical alerts feed the fire-evac roll.
            </DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Occupancy</Label>
                <Select value={editing.occupancy || "owner_occupied"}
                        onValueChange={(v) => setEditing({ ...editing, occupancy: v })}>
                  <SelectTrigger className="mt-1 bg-white/[0.04] border-white/10 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                    <SelectItem value="owner_occupied">Owner-occupied</SelectItem>
                    <SelectItem value="rented">Rented</SelectItem>
                    <SelectItem value="vacant">Vacant</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {editing.occupancy === "rented" && (
                <div className="grid grid-cols-2 gap-2 rounded-md border border-orange-500/20 p-3 bg-orange-500/[0.03]">
                  <div className="col-span-2 overline text-[10px]">Tenant on premises</div>
                  <div>
                    <Label className="text-[10px]">Name</Label>
                    <Input value={editing.tenant?.name || ""}
                            onChange={(e) => setEditing({ ...editing, tenant: { ...editing.tenant, name: e.target.value } })}
                            className="bg-white/[0.04] border-white/10 mt-1 h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-[10px]">Phone</Label>
                    <Input value={editing.tenant?.phone || ""}
                            onChange={(e) => setEditing({ ...editing, tenant: { ...editing.tenant, phone: e.target.value } })}
                            className="bg-white/[0.04] border-white/10 mt-1 h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-[10px]">Lease start</Label>
                    <Input type="date" value={editing.tenant?.lease_start || ""}
                            onChange={(e) => setEditing({ ...editing, tenant: { ...editing.tenant, lease_start: e.target.value } })}
                            className="bg-white/[0.04] border-white/10 mt-1 h-8 text-xs [color-scheme:dark]" />
                  </div>
                  <div>
                    <Label className="text-[10px]">Lease end</Label>
                    <Input type="date" value={editing.tenant?.lease_end || ""}
                            onChange={(e) => setEditing({ ...editing, tenant: { ...editing.tenant, lease_end: e.target.value } })}
                            className="bg-white/[0.04] border-white/10 mt-1 h-8 text-xs [color-scheme:dark]" />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-3 gap-2 rounded-md border border-white/10 p-3">
                <div className="col-span-3 overline text-[10px]">Emergency contact</div>
                <div>
                  <Label className="text-[10px]">Name</Label>
                  <Input value={editing.emergency_contact?.name || ""}
                          onChange={(e) => setEditing({ ...editing, emergency_contact: { ...editing.emergency_contact, name: e.target.value } })}
                          className="bg-white/[0.04] border-white/10 mt-1 h-8 text-xs"
                          data-testid="unit-ec-name" />
                </div>
                <div>
                  <Label className="text-[10px]">Phone</Label>
                  <Input value={editing.emergency_contact?.phone || ""}
                          onChange={(e) => setEditing({ ...editing, emergency_contact: { ...editing.emergency_contact, phone: e.target.value } })}
                          className="bg-white/[0.04] border-white/10 mt-1 h-8 text-xs"
                          data-testid="unit-ec-phone" />
                </div>
                <div>
                  <Label className="text-[10px]">Relationship</Label>
                  <Input value={editing.emergency_contact?.relationship || ""}
                          onChange={(e) => setEditing({ ...editing, emergency_contact: { ...editing.emergency_contact, relationship: e.target.value } })}
                          className="bg-white/[0.04] border-white/10 mt-1 h-8 text-xs"
                          placeholder="spouse, parent, neighbor…"
                          data-testid="unit-ec-relationship" />
                </div>
              </div>

              <div className="rounded-md border border-white/10 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="overline text-[10px]">Medical alerts</div>
                  <Button size="sm" variant="outline" onClick={addAlert}
                          className="h-7 text-xs border-white/10">
                    <Plus className="h-3 w-3 mr-1" /> Add alert
                  </Button>
                </div>
                {(editing.medical_alerts || []).map((a, i) => (
                  <div key={i} className="flex items-center gap-2 mb-2">
                    <Select value={a.kind}
                            onValueChange={(v) => {
                              const next = [...editing.medical_alerts];
                              next[i] = { ...next[i], kind: v };
                              setEditing({ ...editing, medical_alerts: next });
                            }}>
                      <SelectTrigger className="w-32 h-8 bg-white/[0.04] border-white/10 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                        <SelectItem value="mobility">Mobility</SelectItem>
                        <SelectItem value="oxygen">Oxygen</SelectItem>
                        <SelectItem value="hearing">Hearing</SelectItem>
                        <SelectItem value="vision">Vision</SelectItem>
                        <SelectItem value="cognitive">Cognitive</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input value={a.note || ""} placeholder="Notes"
                            onChange={(e) => {
                              const next = [...editing.medical_alerts];
                              next[i] = { ...next[i], note: e.target.value };
                              setEditing({ ...editing, medical_alerts: next });
                            }}
                            className="flex-1 h-8 text-xs bg-white/[0.04] border-white/10" />
                    <button onClick={() => setEditing({ ...editing, medical_alerts: editing.medical_alerts.filter((_, j) => j !== i) })}
                            className="text-white/40 hover:text-rose-400">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                {(!editing.medical_alerts || editing.medical_alerts.length === 0) && (
                  <div className="text-xs text-white/40">No alerts.</div>
                )}
              </div>

              <div>
                <Label className="text-xs">Notes</Label>
                <Textarea value={editing.notes || ""} rows={2}
                          onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                          className="bg-white/[0.04] border-white/10 mt-1 text-sm" />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-white/10">Cancel</Button>
            <Button onClick={save} disabled={busy}
                    className="bg-orange-500 hover:bg-orange-600"
                    data-testid="unit-save-btn">
              <Save className="h-4 w-4 mr-1" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
